import { useEffect, useMemo, useState } from 'react';
import {
  getSupportDiag,
  listSupportDiag,
  type SupportDiagnostic,
} from '../../services/supportDiag';

// Sprint 29 / Fase 29.F — Painel admin pra support flow CLI player.
//
// Lista submissions de player banido (oraculo_support.exe), com filtros
// opcionais por hwid (correlação ban) ou session_uuid. Detalhe expansível
// mostra reason + log_excerpt full.

function formatDateTime(iso: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function truncate(text: string, n: number): string {
  if (!text) return '';
  if (text.length <= n) return text;
  return `${text.slice(0, n)}…`;
}

interface DetailDrawerProps {
  uuid: string | null;
  onClose: () => void;
}

function DetailDrawer({ uuid, onClose }: DetailDrawerProps) {
  const [item, setItem] = useState<SupportDiagnostic | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!uuid) {
      setItem(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getSupportDiag(uuid)
      .then((d) => {
        if (!cancelled) setItem(d);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e?.response?.data?.error?.message || e?.message || 'erro');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uuid]);

  if (!uuid) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-end md:items-center justify-center z-40">
      <div className="bg-[#161a1f] border border-[#2a2f36] rounded-t md:rounded p-6 w-full md:max-w-3xl max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-lg font-bold text-amber-400">Support diagnostic</h2>
            <p className="text-xs text-gray-500 font-mono mt-1">{uuid}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-white text-xl leading-none"
            aria-label="Fechar"
          >
            ×
          </button>
        </div>

        {loading && <p className="text-sm text-gray-500">Carregando…</p>}
        {error && (
          <div className="bg-red-900/30 border border-red-700 text-red-300 px-4 py-2 rounded text-sm">
            {error}
          </div>
        )}

        {item && !loading && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <span className="text-gray-500 block text-xs">Created at</span>
                <span className="font-mono">{formatDateTime(item.created_at)}</span>
              </div>
              <div>
                <span className="text-gray-500 block text-xs">HWID</span>
                <span className="font-mono text-xs">{item.hwid || '—'}</span>
              </div>
              <div>
                <span className="text-gray-500 block text-xs">Build ID</span>
                <span className="font-mono">{item.build_id || '—'}</span>
              </div>
              <div>
                <span className="text-gray-500 block text-xs">SDK version</span>
                <span className="font-mono">{item.sdk_version || '—'}</span>
              </div>
              <div>
                <span className="text-gray-500 block text-xs">Source IP</span>
                <span className="font-mono">{item.source_ip || '—'}</span>
              </div>
              <div className="col-span-2 md:col-span-3">
                <span className="text-gray-500 block text-xs">Session UUID</span>
                <span className="font-mono text-xs">{item.session_uuid || '— (sem session correlata)'}</span>
              </div>
            </div>

            <div>
              <span className="text-gray-500 block text-xs mb-1">Reported reason</span>
              <pre className="bg-[#0d0f12] border border-[#2a2f36] rounded p-3 text-xs whitespace-pre-wrap break-words">
                {item.reported_reason || '(player não escreveu motivo)'}
              </pre>
            </div>

            <div>
              <span className="text-gray-500 block text-xs mb-1">
                Log excerpt (sanitized)
              </span>
              <pre className="bg-[#0d0f12] border border-[#2a2f36] rounded p-3 text-xs font-mono whitespace-pre-wrap break-words max-h-96 overflow-y-auto">
                {item.log_excerpt || '(sem log)'}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function SupportDiag() {
  const [items, setItems] = useState<SupportDiagnostic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hwidFilter, setHwidFilter] = useState('');
  const [sessionFilter, setSessionFilter] = useState('');
  const [refreshTick, setRefreshTick] = useState(0);
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listSupportDiag({
      hwid: hwidFilter.trim() || undefined,
      sessionUuid: sessionFilter.trim() || undefined,
      limit: 100,
    })
      .then((r) => {
        if (!cancelled) setItems(r.data);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e?.response?.data?.error?.message || e?.message || 'erro');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshTick, hwidFilter, sessionFilter]);

  const empty = useMemo(() => !loading && items.length === 0, [loading, items]);

  return (
    <div className="text-gray-200">
      <h1 className="text-2xl font-bold mb-4">Support Diag</h1>
      <p className="text-sm text-gray-400 mb-6">
        Submissions do <code className="bg-[#0d0f12] px-1 rounded">oraculo_support.exe</code> — players banidos contestando.
        Filtre por HWID pra correlacionar com sanctions ativas.
      </p>

      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Filtrar HWID (exato)</label>
          <input
            type="text"
            value={hwidFilter}
            onChange={(e) => setHwidFilter(e.target.value)}
            placeholder="hwid hash"
            className="bg-[#0d0f12] border border-[#2a2f36] rounded px-3 py-2 text-sm font-mono"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Filtrar session UUID</label>
          <input
            type="text"
            value={sessionFilter}
            onChange={(e) => setSessionFilter(e.target.value)}
            placeholder="session_uuid"
            className="bg-[#0d0f12] border border-[#2a2f36] rounded px-3 py-2 text-sm font-mono"
          />
        </div>
        <button
          type="button"
          onClick={() => setRefreshTick((t) => t + 1)}
          className="bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded text-sm font-medium transition"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-900/30 border border-red-700 text-red-300 px-4 py-3 rounded mb-4">
          <span className="font-semibold">Erro:</span> {error}
        </div>
      )}

      {loading && <p className="text-sm text-gray-500">Carregando…</p>}
      {empty && <p className="text-sm text-gray-500">Sem submissions com esses filtros.</p>}

      {!loading && items.length > 0 && (
        <div className="overflow-x-auto bg-[#161a1f] rounded border border-[#2a2f36]">
          <table className="w-full text-sm">
            <thead className="bg-[#0d0f12] text-gray-400 text-xs uppercase">
              <tr>
                <th className="py-2 px-3 text-left">Created at</th>
                <th className="py-2 px-3 text-left">HWID</th>
                <th className="py-2 px-3 text-left">Build / SDK</th>
                <th className="py-2 px-3 text-left">Reason</th>
                <th className="py-2 px-3 text-left">Source IP</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr
                  key={it.id}
                  className="border-b border-[#2a2f36] hover:bg-[#1a1f26]"
                >
                  <td className="py-2 px-3 font-mono text-xs">{formatDateTime(it.created_at)}</td>
                  <td className="py-2 px-3 font-mono text-xs">{it.hwid || '—'}</td>
                  <td className="py-2 px-3 font-mono text-xs">
                    {it.build_id || '—'}
                    {it.sdk_version && (
                      <span className="block text-gray-500">{it.sdk_version}</span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-gray-300">
                    {truncate(it.reported_reason, 60) || <span className="text-gray-500">—</span>}
                  </td>
                  <td className="py-2 px-3 font-mono text-xs">{it.source_ip || '—'}</td>
                  <td className="py-2 px-3 text-right">
                    <button
                      type="button"
                      onClick={() => setSelectedUuid(it.id)}
                      className="text-amber-400 hover:text-amber-300 text-xs"
                    >
                      Ver detalhe →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <DetailDrawer uuid={selectedUuid} onClose={() => setSelectedUuid(null)} />
    </div>
  );
}
